import {TSlot, TTimetableItem} from './timetable.type';

export type LecturePosition = {ttid: number; weight: number; tsid: number};

export type MoveLectureResult = {
    nextLectures: Record<number, TTimetableItem[]>;
    positions: LecturePosition[];
};

export const findLectureDay = (
    lectures: Record<number, TTimetableItem[]>,
    ttid: number
): [number, TTimetableItem[]] | null => {
    const entry = Object.entries(lectures).find(([, items]) => items.some(l => l.ttid === ttid));
    return entry ? [Number(entry[0]), entry[1]] : null;
};

const diffPositions = (
    currentLectures: TTimetableItem[],
    newWeights: Map<number, number>
): LecturePosition[] => {
    const result: LecturePosition[] = [];
    newWeights.forEach((weight, ttid) => {
        const current = currentLectures.find(l => l.ttid === ttid);
        if(current && current.weight !== weight && current.tsid != null) {
            result.push({ttid, weight, tsid: current.tsid});
        }
    });
    return result;
};

/**
 * @param orderedTtidsInTargetSlot - new ordering of the target slot, INCLUDING the moved ttid.
 *                                   Each ttid's index becomes its new weight.
 */
export const computeMoveLecture = (
    state: Record<number, TTimetableItem[]>,
    slots: TSlot[],
    ttid: number,
    targetTsid: number,
    targetDayKey: number,
    orderedTtidsInTargetSlot: number[]
): MoveLectureResult | null => {
    const source = findLectureDay(state, ttid);
    if(!source) return null;
    const [sourceDayKey, sourceDayLectures] = source;
    const lecture = sourceDayLectures.find(l => l.ttid === ttid);
    if(!lecture) return null;

    let newTime: number;
    if(targetTsid === 0) {
        newTime = 0;
    } else {
        const targetSlot = slots.find(s => s.tsid === targetTsid);
        if(!targetSlot) return null;
        newTime = targetSlot.timeStart;
    }

    const sourceTsid = lecture.tsid;
    const targetLectures = state[targetDayKey] ?? [];

    const targetWeightByTtid = new Map(orderedTtidsInTargetSlot.map((id, w) => [id, w]));
    const newWeight = targetWeightByTtid.get(ttid) ?? 0;
    const movedLecture: TTimetableItem = {
        ...lecture,
        time: newTime,
        weight: newWeight,
        tsid: targetTsid,
    };

    const sourceWeightByTtid = new Map<number, number>();
    if(sourceTsid !== targetTsid) {
        sourceDayLectures
            .filter(l => l.ttid !== ttid && l.tsid === sourceTsid)
            .sort((a, b) => (a.weight ?? 0) - (b.weight ?? 0))
            .forEach((l, idx) => sourceWeightByTtid.set(l.ttid, idx));
    }

    const reweight = (l: TTimetableItem): TTimetableItem => {
        if(l.ttid === ttid) return movedLecture;
        const targetWeight = targetWeightByTtid.get(l.ttid) ?? sourceWeightByTtid.get(l.ttid);
        return targetWeight === undefined ? l : {...l, weight: targetWeight};
    };

    const nextLectures = {...state};
    if(sourceDayKey === targetDayKey) {
        nextLectures[targetDayKey] = sourceDayLectures.map(reweight);
    } else {
        nextLectures[sourceDayKey] = sourceDayLectures.filter(l => l.ttid !== ttid).map(reweight);
        nextLectures[targetDayKey] = [...targetLectures.map(reweight), movedLecture];
    }

    const positions: LecturePosition[] = [
        {ttid, weight: newWeight, tsid: targetTsid},
        ...diffPositions(targetLectures, targetWeightByTtid).filter(p => p.ttid !== ttid),
        ...diffPositions(sourceDayLectures, sourceWeightByTtid),
    ];

    return {nextLectures, positions};
};
